/**
 * Jovii — PLAYER, how much of the stage a panel has taken.
 *
 * ── WHY THIS EXISTS ────────────────────────────────────────────────────────────
 *
 * The room chat used to be a drawer drawn ON TOP of the film: 340 px of glass over
 * the right-hand third of the picture on a tablet, a sheet over the bottom two
 * thirds on a phone. The owner asked for the opposite — *"have the opening of the
 * chat actually shrink the video being played… I want the chat to push the stream
 * to the left, making it smaller if it needs to"*. Nothing may ever be over the
 * picture, and nothing may crop it either: the film letterboxes into whatever is
 * left, which is what `object-fit: contain` on a narrower box already does for free.
 *
 * So the player needs to know how much of its stage has been claimed. It cannot be
 * told by a prop: the chat drawer is a SIBLING of the player (deliberately — see
 * `routes/Watch.svelte`, which keeps it outside the player's chrome, idle timer and
 * full-screen element), so neither component has a reference to the other. This is
 * the same shape of problem `chrome.ts` solves, and the same answer: a module
 * singleton both of them can see.
 *
 * ── ONE OWNER OF THE NUMBER ────────────────────────────────────────────────────
 *
 * The panel that covers the film is the only thing that knows how big it is, so the
 * panel claims; the stage reads. Nothing else writes. (Two owners for one number is
 * how this codebase got a 268 px offset and a 91 px overflow — see
 * `calls/CallDock.svelte`.) Claims are keyed so a second panel cannot silently
 * clobber the first, and the published value is the largest claim on each edge.
 *
 * ── AND ONE FACT THAT IS THE DOCUMENT'S, NOT ANY COMPONENT'S ───────────────────
 *
 * While a DOM full screen is up, the reservation is suppressed and this publishes
 * zero. That is not a policy decision, it is geometry: a full-screen element paints
 * in the top layer above everything outside its own subtree, so a drawer that is a
 * sibling of the player is simply not on screen. Reserving space for a panel nobody
 * can see would put a black stripe down the side of a full-screen film. The
 * document is the thing that knows, so the document is what is listened to here
 * rather than making every reader ask the player.
 *
 * The iPhone's own full-screen player is a different thing again — it is not a DOM
 * full screen at all, `document.fullscreenElement` stays null, and none of our DOM
 * is painted while it is up. Nothing to do: whatever this says is invisible.
 */

export interface StageInset {
  /** Pixels claimed off the right-hand edge of the stage. */
  right: number;
  /** Pixels claimed off the bottom edge of the stage. */
  bottom: number;
}

export const NO_INSET: StageInset = { right: 0, bottom: 0 };

export type StageInsetListener = (inset: StageInset) => void;

const FULLSCREEN_EVENTS = ['fullscreenchange', 'webkitfullscreenchange'] as const;

function domFullscreen(): boolean {
  if (typeof document === 'undefined') return false;
  const d = document as Document & { webkitFullscreenElement?: Element | null };
  const active = d.fullscreenElement ?? d.webkitFullscreenElement ?? null;
  // The persistent watch root includes chat, so its claim stays visible.
  return active !== null && !active.hasAttribute?.('data-watch-viewport');
}

export class StageInsets {
  private readonly claims = new Map<string, StageInset>();
  private readonly listeners = new Set<StageInsetListener>();
  private published: StageInset = { ...NO_INSET };
  private suppressed = false;
  private watching = false;

  /** What the stage should actually reserve right now. */
  get current(): StageInset {
    return this.published;
  }

  /** What has been claimed, ignoring whether it is currently suppressed. Diagnostics. */
  get claimed(): StageInset {
    return this.combine();
  }

  /**
   * Svelte store contract, like `chrome.subscribe`: one line in an `$effect` both
   * subscribes and unsubscribes, and the immediate call means a component that
   * mounts while a panel is already open starts in the right state instead of
   * animating in from zero.
   */
  subscribe(fn: StageInsetListener): () => void {
    this.watch();
    this.listeners.add(fn);
    fn(this.published);
    return () => {
      this.listeners.delete(fn);
    };
  }

  /** A panel is covering this much of the stage. Idempotent. */
  set(key: string, inset: Partial<StageInset>): void {
    const next: StageInset = {
      right: Math.max(0, Math.round(inset.right ?? 0)),
      bottom: Math.max(0, Math.round(inset.bottom ?? 0))
    };
    const now = this.claims.get(key);
    if (now !== undefined && now.right === next.right && now.bottom === next.bottom) return;
    this.claims.set(key, next);
    this.recompute();
  }

  /** That panel has gone. */
  clear(key: string): void {
    if (!this.claims.delete(key)) return;
    this.recompute();
  }

  /** Everything has gone — the screen is being torn down. */
  reset(): void {
    if (this.claims.size === 0) return;
    this.claims.clear();
    this.recompute();
  }

  private combine(): StageInset {
    let right = 0;
    let bottom = 0;
    for (const c of this.claims.values()) {
      if (c.right > right) right = c.right;
      if (c.bottom > bottom) bottom = c.bottom;
    }
    return { right, bottom };
  }

  private recompute(): void {
    const next = this.suppressed ? NO_INSET : this.combine();
    if (next.right === this.published.right && next.bottom === this.published.bottom) return;
    this.published = { right: next.right, bottom: next.bottom };
    for (const fn of this.listeners) fn(this.published);
  }

  /**
   * Watch the document for a full screen coming and going. Installed on the first
   * subscription rather than at import time, so importing this module in a test
   * runner with no DOM does nothing at all.
   */
  private watch(): void {
    if (this.watching || typeof document === 'undefined') return;
    this.watching = true;
    this.suppressed = domFullscreen();
    const onChange = (): void => {
      const next = domFullscreen();
      if (next === this.suppressed) return;
      this.suppressed = next;
      this.recompute();
    };
    for (const e of FULLSCREEN_EVENTS) document.addEventListener(e, onChange);
  }
}

/**
 * The tab's stage reservation. A panel over the film claims; the player's stage and
 * the call window both read it — the call window because its position is clamped
 * into the viewport, and the film's area is no longer the whole viewport.
 */
export const stageInset = new StageInsets();

/**
 * The nearest ancestor (or the node itself) that floats over the page rather than
 * taking part in its layout, or null when there is none.
 *
 * This is how a panel decides whether it is covering the film at all. Dropped into
 * an ordinary column — the chat bench does exactly that — it is laid out like
 * anything else, takes no space from anybody, and must claim nothing. Fixed to an
 * edge of the viewport, it is over the picture and has to say so.
 */
export function floatingHost(node: HTMLElement): HTMLElement | null {
  if (typeof getComputedStyle === 'undefined') return null;
  let el: HTMLElement | null = node;
  while (el !== null && el !== document.body) {
    if (getComputedStyle(el).position === 'fixed') return el;
    el = el.parentElement;
  }
  return null;
}

/**
 * Which edge a floating panel is docked to, and how much of the stage that costs.
 *
 * Decided from the panel's own size and not from its position, because position is
 * a lie while it is sliding: the drawer animates in on a `transform`, so its
 * bounding rect is off the edge of the screen for the first 320 ms. `offsetWidth`
 * and `offsetHeight` ignore transforms, so the answer is right from the first frame
 * and the film shrinks in step with the panel arriving rather than after it.
 *
 * A panel as wide as the viewport is a bottom sheet and costs height; anything
 * narrower is a column beside the film and costs width. That is the phone/tablet
 * split the drawer's own media query makes, read off the result rather than
 * duplicated here — a media query written twice is two owners of one decision.
 */
export function reservedBy(panel: HTMLElement, viewportWidth: number): StageInset {
  const w = panel.offsetWidth;
  const h = panel.offsetHeight;
  if (w <= 0 || h <= 0) return { ...NO_INSET };
  return w >= viewportWidth - 2 ? { right: 0, bottom: h } : { right: w, bottom: 0 };
}
